import ms from 'ms';
import * as fs from 'node:fs';
import * as path from 'node:path';
import * as config from './config';
import * as localRunnerCache from './runnerFileCache';
import { serializeError } from 'serialize-error';
import { getLogger } from './logger';
import { additionalLogDetails } from './logUtils';
import { ArgError, NpmPermissionsError } from '../errors';
import {
    getCliLocation,
    isURL,
    downloadAndSave,
    getSource,
    getLocalFileSizeInMB,
    download,
    unzipFile,
    getSourcePath,
    promiseMap,
} from '../utils';

const logger = getLogger('prepare runner and testim start');
const MSEC_IN_HALF_DAY = ms('0.5 day');
const MAX_CUSTOM_EXT_SIZE_MB = 16;
const MAX_CUSTOM_SIZE_ERROR_MSG = `The size of the custom extension is more than ${MAX_CUSTOM_EXT_SIZE_MB}MB`;

export async function prepareCustomExtension(location: string, unlimitedSize = false) {
    if (!location) {
        return undefined;
    }

    if (isURL(location)) {
        const destFile = path.join(process.cwd(), location.replace(/^.*[\\/]/, ''));
        const contentLength = await getRemoteFileSizeInMB(location);
        if (contentLength > MAX_CUSTOM_EXT_SIZE_MB && !unlimitedSize) {
            throw new ArgError(MAX_CUSTOM_SIZE_ERROR_MSG);
        }
        await downloadAndSave(location, destFile);
        return destFile;
    }

    const destFile = path.resolve(location);
    if (!fs.existsSync(destFile)) {
        throw new ArgError(`Failed to find custom extension in location: ${destFile}`);
    }
    const fileSize = getLocalFileSizeInMB(destFile);
    if (fileSize > MAX_CUSTOM_EXT_SIZE_MB && !unlimitedSize) {
        throw new ArgError(MAX_CUSTOM_SIZE_ERROR_MSG);
    }
    return destFile;
}

async function getRemoteFileSizeInMB(url: string) {
    const httpRequest = require('./httpRequest');
    try {
        const res = await httpRequest.head(url);
        const contentLengthHeader = res.headers['content-length'];
        const contentLengthBytes = contentLengthHeader ? parseInt(contentLengthHeader, 10) : 0;
        return contentLengthBytes / 1000000;
    } catch (err) {
        logger.warn('failed to download custom extension', { err });
        throw new ArgError(`Failed to download custom extension from location: ${url}`);
    }
}

export function prepareExtension(locations: string[]) {
    logger.info('prepare extension', { locations });

    const fullLocations = locations.map((location) => ({ location, path: getSourcePath(location) }));
    return localRunnerCache.memoize(
        () => promiseMap(fullLocations, ({ location, path: _path }) => getSource(location, _path)),
        'prepareExtension',
        MSEC_IN_HALF_DAY,
        fullLocations,
    )();
}

export async function prepareChromeDriver(
    userDetails: object = {},
    driverOptions: { chromeBinaryLocation?: string } = {},
    skipIsReadyCheck = false,
) {
    const ora = require('ora') as typeof import('ora');
    const spinner = ora('Starting Driver').start();
    const chromedriverWrapper = await import('./chromedriverWrapper');

    try {
        await chromedriverWrapper.install();
        await chromedriverWrapper.start();
        if (!skipIsReadyCheck) {
            await chromedriverWrapper.isReady(driverOptions);
        }
        spinner.succeed('Chrome Driver initiated successfully');
    } catch (error) {
        const errorMsg = 'Failed to initiate Chrome Driver';

        if (!(error instanceof NpmPermissionsError)) {
            //NpmPermissionsError was printed and logged already
            logger.error(errorMsg, {
                ...userDetails,
                ...additionalLogDetails(),
                error: serializeError(error),
            });
            // eslint-disable-next-line no-console
            console.log(`
1. If you don't have Chrome, please install it from https://www.google.com/chrome.
2. If Chrome is installed, please verify it's binary directory:
    - installed where chromedriver expects it (see https://github.com/SeleniumHQ/selenium/wiki/ChromeDriver#requirements).
    - exists in your PATH environment variables.
3. Try adding --chrome-binary-location flag to Testim CLI specifying the exact location of chrome binary in your computer (e.g on Windows "C:/Program Files/Google/Chrome/Application/chrome.exe").
4. You can always use a standalone Selenium grid and providing it's details with the --host and --port flags (see https://www.npmjs.com/package/selenium-standalone)`);
        }

        spinner.fail(errorMsg);
        throw error;
    }
}

async function getPlayerVersion() {
    const url = `${config.EXTENSION_SP_S3_HOST}/sessionPlayer_LATEST_RELEASE`;
    const res = await download(url);
    return res.body.toString('utf8');
}

async function getPlayerLocation(location: string, canary?: boolean) {
    if (!isURL(location) || (isURL(location) && canary) || config.IS_ON_PREM) {
        return location;
    }

    const ver = await getPlayerVersion();
    return `${location}-${ver}`;
}

export function getSessionPlayerFolder() {
    const cliLocation = getCliLocation();

    return path.resolve(cliLocation, 'testim-bin');
}

function getPlayerDestination() {
    const testimAppData = getSessionPlayerFolder();

    const playerDestination = path.resolve(testimAppData, 'sessionPlayer.zip');

    return playerDestination;
}

async function downloadAndUnzip(
    loc: string,
    playerFileName: string,
    isRetry = false,
): Promise<Awaited<ReturnType<typeof unzipFile>>> {
    try {
        await getSource(loc, playerFileName);
        return await unzipFile(playerFileName, getSessionPlayerFolder());
    } catch (err) {
        if (isRetry) {
            throw err;
        }
        return await downloadAndUnzip(loc, playerFileName, true);
    }
}

export async function preparePlayer(location: string, canary?: boolean) {
    logger.info('prepare player', { location, canary });
    const playerFileName = getPlayerDestination();
    return localRunnerCache.memoize(
        async () => {
            const loc = await getPlayerLocation(location, canary);
            await downloadAndUnzip(loc, playerFileName);
            // return a truthy value, so the caching has a value, and doesn't ignore
            return {};
        },
        'preparePlayer',
        MSEC_IN_HALF_DAY,
        [location, canary, playerFileName],
    )();
}
